import React, { useEffect, useState } from "react";
import notify from "./common/notify";
import "./ModalPagoSemimasivo.css";
import { processPaymentsBulk } from "../services/deudaServices";
import { procesarDevengoComision } from "../services/procesarDevengoComision";
import { cerrarPromesasPorPago } from "../services/promesasCumplimientoAuto";
import { METODOS_PAGO, normalizeMetodoPago } from "../helpers/metodosPago";
import { toJsDate } from "../helpers/fechas";

const metodosValidos = METODOS_PAGO.filter((item) => item.value !== "historico").map((item) => item.value);

const addOneMonth = (date) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1);
  return d;
};

const formatDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseDateInput = (value) => {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const monthsBetweenInclusive = (start, end) => {
  if (!start || !end) return 1;

  const s = new Date(start);
  const e = new Date(end);

  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);

  let months =
    (e.getFullYear() - s.getFullYear()) * 12 +
    (e.getMonth() - s.getMonth());

  if (e.getDate() >= s.getDate()) {
    months += 1;
  }

  return Math.max(months, 1);
};

const getSuggestedStartDate = (deuda) => {
  const nextDue = toJsDate(deuda?.nextDueDate);
  if (nextDue) return formatDate(nextDue);

  const pagos = Array.isArray(deuda?.pagos) ? deuda.pagos : [];
  const pagosPositivos = pagos.filter((p) => Number(p?.cantidad || 0) > 0 && toJsDate(p?.fecha));

  if (pagosPositivos.length > 0) {
    const ultimoPago = pagosPositivos
      .map((p) => toJsDate(p.fecha))
      .sort((a, b) => b - a)[0];

    const siguiente = addOneMonth(ultimoPago);
    return formatDate(siguiente);
  }

  const creada = deuda?.creada?.toDate?.();
  if (creada) return formatDate(creada);

  return formatDate(new Date());
};

export default function ModalPagoSemimasivo({
  isOpen,
  onClose,
  onSaved,
  deuda,
  cliente,
  deudaId,
}) {
  const [fechaInicio, setFechaInicio] = useState("");
  const [cantidadPeriodos, setCantidadPeriodos] = useState(1);
  const [montoBase, setMontoBase] = useState(deuda?.mensualidad || 0);
  const [metodoBase, setMetodoBase] = useState("efectivo");
  const [referenciaBase, setReferenciaBase] = useState("");
  const [notasBase, setNotasBase] = useState("");
  const [rows, setRows] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const inicioSugerido = getSuggestedStartDate(deuda);
    const hoy = new Date();
    const fechaInicioDate = parseDateInput(inicioSugerido);
    const periodosSugeridos = monthsBetweenInclusive(fechaInicioDate, hoy);

    setFechaInicio(inicioSugerido);
    setCantidadPeriodos(periodosSugeridos);
    setMontoBase(Number(deuda?.mensualidad) || 0);
    setMetodoBase("efectivo");
    setReferenciaBase("");
    setNotasBase("");
    setRows([]);
    setIsSubmitting(false);
  }, [isOpen, deuda]);

  useEffect(() => {
    if (!fechaInicio) return;

    const fechaInicioDate = parseDateInput(fechaInicio);
    const hoy = new Date();
    const periodosSugeridos = monthsBetweenInclusive(fechaInicioDate, hoy);

    setCantidadPeriodos(periodosSugeridos);
  }, [fechaInicio]);

  // A11Y (Sprint A11Y): cerrar con Escape (el modal ya tiene role=dialog).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !isSubmitting) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const generarTabla = () => {
    if (!fechaInicio) {
      notify.error("Selecciona una fecha inicial");
      return;
    }

    if (!cantidadPeriodos || cantidadPeriodos < 1) {
      notify.error("Ingresa una cantidad válida de periodos");
      return;
    }

    const baseDate = new Date(`${fechaInicio}T00:00:00`);
    const nuevasFilas = [];
    let current = new Date(baseDate);

    for (let i = 0; i < cantidadPeriodos; i++) {
      nuevasFilas.push({
        id: `${i}_${Date.now()}`,
        numero: i + 1,
        selected: true,
        fechaEsperada: formatDate(current),
        fechaReal: formatDate(current),
        cantidad: Number(montoBase) || 0,
        metodo: metodoBase,
        referencia: referenciaBase,
        notas: notasBase,
      });

      current = addOneMonth(current);
    }

    setRows(nuevasFilas);
  };

  const updateRow = (index, field, value) => {
    const copy = [...rows];
    copy[index][field] = value;
    setRows(copy);
  };

  const toggleRow = (index) => {
    const copy = [...rows];
    copy[index].selected = !copy[index].selected;
    setRows(copy);
  };

  const marcarTodos = (value) => {
    const copy = rows.map((r) => ({ ...r, selected: value }));
    setRows(copy);
  };

  const totalSeleccionado = rows
    .filter((r) => r.selected)
    .reduce((s, r) => s + Number(r.cantidad || 0), 0);

  const periodosGenerados = rows.length;
  const pagosSeleccionados = rows.filter((r) => r.selected).length;
  const mesesOmitidos = Math.max(periodosGenerados - pagosSeleccionados, 0);

  const confirmar = async () => {
    const seleccionados = rows.filter((r) => r.selected);

    if (!seleccionados.length) {
      notify.error("Selecciona al menos un pago");
      return;
    }

    const invalidos = seleccionados.some(
      (r) =>
        !r.fechaReal ||
        !metodosValidos.includes(r.metodo) ||
        Number(r.cantidad || 0) <= 0
    );

    if (invalidos) {
      notify.error("Revisa los pagos seleccionados. Hay fechas, montos o métodos inválidos.");
      return;
    }

    const sinReferenciaTransferencia = seleccionados.some(
      (r) => normalizeMetodoPago(r.metodo).transferencia && !String(r.referencia || "").trim()
    );
    if (sinReferenciaTransferencia) {
      notify.error("Los pagos por transferencia o deposito requieren referencia.");
      return;
    }

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const bulkResult = await processPaymentsBulk(
        deudaId,
        seleccionados.map((r) => ({
          fechaReal: r.fechaReal,
          cantidad: Number(r.cantidad),
          metodo: r.metodo,
          referencia: r.referencia || "",
          notas: r.notas || "",
        }))
      );

      // DEVENGO-FIX (17 jun 2026): llamar procesarDevengoComision UNA VEZ por
      // cada pago nuevo escrito, igual que el flujo individual. Antes había
      // 1 sola llamada al final del bulk que devolvía devengados=0 sin error
      // visible — el operador no se enteraba y los hitos quedaban sin devengar.
      // La función es idempotente, así que llamarla N veces NO duplica CxPs.
      let totalDevengados = 0;
      const erroresAcum = [];
      const pagoIds = Array.isArray(bulkResult?.pagoIds) ? bulkResult.pagoIds : [];
      for (const pagoId of pagoIds) {
        try {
          const r = await procesarDevengoComision(deudaId, pagoId);
          if (r?.devengados) totalDevengados += r.devengados;
          if (r?.errores?.length) erroresAcum.push(...r.errores);
        } catch (e) {
          console.error("procesarDevengoComision (Semimasivo) pago", pagoId, e);
          erroresAcum.push(`Pago ${pagoId}: ${e?.message || "error"}`);
        }
      }
      if (totalDevengados > 0) {
        notify.success(
          `${totalDevengados} hito${totalDevengados > 1 ? "s" : ""} de comisión devengado${totalDevengados > 1 ? "s" : ""} y CxP generada${totalDevengados > 1 ? "s" : ""}.`
        );
      }
      if (erroresAcum.length) {
        notify.error(
          `Pagos OK, pero devengo reportó errores: ${erroresAcum.slice(0, 2).join(" | ")}`
        );
      }

      const primeraFecha = pagosSeleccionados > 0 ? (rows[0]?.fechaReal || new Date()) : new Date();
      try {
        const n = await cerrarPromesasPorPago(deudaId, primeraFecha);
        if (n > 0) notify.success(`${n} promesa(s) marcadas como cumplidas`);
      } catch (e) {
        console.error("cerrarPromesasPorPago (Semimasivo):", e);
        notify.error(
          `Pagos registrados, pero el cierre de promesas falló: ${e?.message || "error"}. Revisa Seguimiento de Cobranza.`
        );
      }

      notify.success(
        `Se registraron ${pagosSeleccionados} pagos de ${periodosGenerados} periodos. Meses omitidos: ${mesesOmitidos}.`
      );

      if (onSaved) {
        await onSaved();
      }

      onClose();
    } catch (err) {
      console.error("Error en registro semimasivo:", err);
      notify.error(err?.message || "No se pudieron registrar los pagos");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-semi-backdrop" role="dialog" aria-modal="true">
      <div className="modal-semi">
        <div className="modal-semi-header">
          <h2>Registro Semimasivo de Pagos</h2>

          <div className="semi-info">
          <p>
            <strong>Cliente:</strong> {cliente?.nombre} {cliente?.apellidos}
          </p>
          <p>
            <strong>Concepto:</strong> {deuda?.concepto}
          </p>
          <p>
            <strong>Mensualidad:</strong>{" "}
{Number(deuda?.mensualidad || 0).toLocaleString("es-MX", {
  style: "currency",
  currency: "MXN",
})}
          </p>
          <p>
            <strong>Deuda ID:</strong> {deudaId}
          </p>
          </div>
        </div>

        <div className="modal-semi-body">
          <div className="semi-form">
          <label>
            Fecha inicio
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              disabled={isSubmitting}
            />
          </label>

          <label>
            Periodos a generar
            <input
              type="number"
              value={cantidadPeriodos}
              min="1"
              onChange={(e) => setCantidadPeriodos(Number(e.target.value))}
              disabled={isSubmitting}
            />
          </label>

          <label>
            Monto base
            <input
  type="number"
  step="any"
  min="0"
  value={montoBase}
  onChange={(e) => setMontoBase(e.target.value)}
  disabled={isSubmitting}
/>
          </label>

          <label>
            Método
            <select
              value={metodoBase}
              onChange={(e) => setMetodoBase(e.target.value)}
              disabled={isSubmitting}
            >
              {METODOS_PAGO.filter((item) => item.value !== "historico").map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Referencia base
            <input
              value={referenciaBase}
              onChange={(e) => setReferenciaBase(e.target.value)}
              disabled={isSubmitting}
            />
          </label>

          <label>
            Notas base
            <input
              value={notasBase}
              onChange={(e) => setNotasBase(e.target.value)}
              disabled={isSubmitting}
            />
          </label>
        </div>

        <button className="btn-generar" onClick={generarTabla} disabled={isSubmitting}>
          Generar tabla
        </button>

        {rows.length > 0 && (
          <>
            <div className="semi-actions">
              <button type="button" onClick={() => marcarTodos(true)} disabled={isSubmitting}>
                Marcar todos
              </button>
              <button type="button" onClick={() => marcarTodos(false)} disabled={isSubmitting}>
                Desmarcar todos
              </button>
            </div>

            <div className="semi-table-container">
              <table className="semi-table">
                <thead>
                  <tr>
                    <th scope="col" className="col-check"></th>
                    <th scope="col" className="col-num">#</th>
                    <th scope="col">Fecha esperada</th>
                    <th scope="col">Fecha real de pago</th>
                    <th scope="col">Monto</th>
                    <th scope="col">Método</th>
                    <th scope="col">Referencia</th>
                    <th scope="col">Notas</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id} className={!r.selected ? "row-unselected" : ""}>
                      <td className="col-check">
                        <input
                          type="checkbox"
                          checked={r.selected}
                          onChange={() => toggleRow(i)}
                          disabled={isSubmitting}
                        />
                      </td>

                      <td className="col-num">{r.numero}</td>

                      <td>
                        <input
                          type="date"
                          value={r.fechaEsperada}
                          onChange={(e) => updateRow(i, "fechaEsperada", e.target.value)}
                          disabled={isSubmitting}
                        />
                      </td>

                      <td>
                        <input
                          type="date"
                          value={r.fechaReal}
                          onChange={(e) => updateRow(i, "fechaReal", e.target.value)}
                          disabled={isSubmitting}
                        />
                      </td>

                      <td>
                        <input
  type="number"
  step="any"
  min="0"
  value={r.cantidad}
  onChange={(e) =>
    updateRow(i, "cantidad", e.target.value)
  }
  disabled={isSubmitting}
/>
                      </td>

                      <td>
                        <select
                          value={r.metodo}
                          onChange={(e) => updateRow(i, "metodo", e.target.value)}
                          disabled={isSubmitting}
                        >
                          {METODOS_PAGO.filter((item) => item.value !== "historico").map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td>
                        <input
                          value={r.referencia}
                          onChange={(e) => updateRow(i, "referencia", e.target.value)}
                          disabled={isSubmitting}
                        />
                      </td>

                      <td>
                        <input
                          value={r.notas}
                          onChange={(e) => updateRow(i, "notas", e.target.value)}
                          disabled={isSubmitting}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </>
        )}
        </div>

        <div className="modal-semi-footer">
          <div className="semi-summary">
            <div><strong>Periodos:</strong> {periodosGenerados}</div>
            <div><strong>Seleccionados:</strong> {pagosSeleccionados}</div>
            <div><strong>Omitidos:</strong> {mesesOmitidos}</div>
            <div><strong>Total:</strong> ${totalSeleccionado.toLocaleString()}</div>
          </div>
          <div className="modal-buttons">
            <button className="btn-cancelar" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </button>
            <button className="btn-guardar" onClick={confirmar} disabled={isSubmitting}>
              {isSubmitting ? "Registrando..." : "Registrar pagos"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
